import { useState } from "react";
import { RiExternalLinkLine, RiArrowRightLine } from "@remixicon/react";
import theme from "@/theme/theme";

interface AppShowcaseCardProps {
  app: {
    id: string;
    name: string;
    description: string;
    image: string;
    link: string;
    tags: string[];
  };
}

export function AppShowcaseCard({ app }: AppShowcaseCardProps) {
  const [imageError, setImageError] = useState(false);

  return (
    <a
      href={app.link}
      target="_blank"
      rel="noopener noreferrer"
      className="group block"
    >
      <div className={`relative overflow-hidden rounded-xl bg-sidebar border border-border hover:border-[${theme.colors.primary.hex}]/50 transition-all duration-300 h-full`}>
        {/* Card top accent */}
        <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[${theme.colors.primary.hex}] to-[${theme.colors.primary.hex}]/70 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300`}></div>

        {/* Card content */}
        <div className="p-6 flex flex-col h-full">
          {/* App icon/logo */}
          <div className="mb-5 flex items-center">
            <div className={`w-12 h-12 rounded-lg bg-muted flex items-center justify-center mr-3 overflow-hidden group-hover:bg-[${theme.colors.primary.hex}]/10 transition-all duration-300`}>
              {!imageError ? (
                <img
                  src={app.image}
                  alt={app.name}
                  width={48}
                  height={48}
                  className="w-full h-full object-contain"
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className={`text-xl font-bold text-[${theme.colors.primary.hex}]`}>{app.name.charAt(0)}</div>
              )}
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-center">
                <h3 className={`text-lg font-semibold group-hover:text-[${theme.colors.primary.hex}] transition-colors`}>
                  {app.name}
                </h3>
                <RiArrowRightLine className={`size-5 text-muted-foreground group-hover:text-[${theme.colors.primary.hex}] transform translate-x-0 group-hover:translate-x-1 transition-all duration-300`} />
              </div>
            </div>
          </div>

          {/* Description */}
          <p className="text-sm text-muted-foreground mb-6 flex-grow">
            {app.description}
          </p>

          {/* Tags */}
          <div className="mt-auto flex items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {app.tags.slice(0, 2).map(tag => (
                <span key={tag} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                  {tag}
                </span>
              ))}
            </div>
            <span className={`text-xs text-muted-foreground group-hover:text-[${theme.colors.primary.hex}] transition-colors flex items-center`}>
              Explore
              <RiExternalLinkLine className="ml-1 size-3" />
            </span>
          </div>
        </div>
      </div>
    </a>
  );
}
